import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { passwordSchema, validate } from '../../utils/validators';
import { logger, generateRequestId } from '../../utils/logger';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { updatePassword, user } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Check if user has valid session (from password reset link)
  useEffect(() => {
    if (!user) {
      logger?.warn('Reset password accessed without valid session');
      navigate('/forgot-password', { replace: true });
    }
  }, [user, navigate]);

  const validateForm = () => {
    const newErrors = {};

    // Password validation
    const passwordValidation = validate(passwordSchema, formData?.password);
    if (!passwordValidation?.success) {
      newErrors.password = passwordValidation?.errors;
    }

    // Confirm password validation
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    
    // Input sanitization - remove any control characters
    const sanitizedValue = value?.replace(/[\r\n\0]/g, '');
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear error for this field when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setGeneralError('');
    
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { error } = await updatePassword(formData?.password);
      
      if (error) {
        if (error?.message?.includes('same')) {
          setGeneralError('New password must be different from your previous password.');
        } else {
          setGeneralError('Failed to update password. Please try again.');
        }
        logger?.warn('Password update failed', { error: error?.message, requestId });
      } else {
        setIsSuccess(true);
        logger?.audit('PASSWORD_RESET_COMPLETED', user?.id, { requestId });
        
        // Redirect to sign in after 3 seconds
        setTimeout(() => {
          navigate('/sign-in', { replace: true });
        }, 3000);
      }
    } catch (error) {
      logger?.error('Password reset error', error, { requestId });
      setGeneralError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <>
        <Helmet>
          <title>Password Reset Successful - MyBodyToday</title>
        </Helmet>

        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-therapeutic-lg border border-border p-6 sm:p-8 max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <Icon name="CheckCircle" size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Password Reset Successful!
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              Your password has been updated successfully. You can now sign in with your new password.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="Loader2" size={16} className="animate-spin" />
              <span>Redirecting to sign in...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reset Password - MyBodyToday</title>
        <meta name="description" content="Create a new secure password for your account" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Main Card */}
          <div className="bg-card rounded-lg shadow-therapeutic-lg border border-border p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-4">
                <Icon name="Lock" size={24} className="text-teal-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Reset Your Password
              </h1>
              <p className="text-sm text-muted-foreground">
                Create a new secure password for your account.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error Message */}
              {generalError && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                  <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{generalError}</p>
                </div>
              )}

              {/* Password Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Icon name="Info" size={16} className="text-blue-600" />
                  Password Requirements
                </h3>
                <ul className="space-y-1 text-xs text-blue-700">
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={12} />
                    At least 8 characters long
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={12} />
                    Contains uppercase and lowercase letters
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={12} />
                    Contains at least one number
                  </li>
                </ul>
              </div>

              {/* New Password Input */}
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label="New Password"
                  placeholder="Enter your new password"
                  value={formData?.password}
                  onChange={handleChange}
                  error={errors?.password}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  maxLength={128}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={loading}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>

              {/* Confirm Password Input */}
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  label="Confirm New Password"
                  placeholder="Re-enter your new password"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  error={errors?.confirmPassword}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  maxLength={128}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  disabled={loading}
                >
                  <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={loading}
                size="lg"
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>

            {/* Security Note */}
            <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-start gap-3">
                <Icon name="Shield" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-amber-900 mb-1">
                    Security Tip
                  </h3>
                  <p className="text-xs text-amber-700">
                    Choose a strong, unique password that you don't use for other accounts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;